import { Component, OnInit, OnDestroy } from '@angular/core';
import { SpinnerService } from 'src/app/services/spinner/spinner.service';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { Profile } from 'src/app/models/profile';
import { BRAND_FAVICON, LOGO_CHAT_SPINNER } from 'src/app/constants';
import { IAIBody, OpenAIRole } from 'src/app/models/chatAiMessage';
import { ChatAIService } from 'src/app/services/chat-ai/chat-ai.service';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ModalController } from '@ionic/angular';
import { ModalSummaryTable } from 'src/app/components/modal-summary-table/modal-summary-table.component';
import { Subscription } from 'rxjs';
import { delay, take } from 'rxjs/operators';
import { AlertService } from 'src/app/services/alert/alert.service';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { IRouteObjectDTO } from '../send/send-objects/send-objects.page';
import { Storage } from '@ionic/storage';

@Component({
  selector: 'app-chat-ai',
  templateUrl: './chat-ai.page.html',
  styleUrls: ['./chat-ai.page.scss'],
})
export class ChatAIPage implements OnInit, OnDestroy {
  profile: Profile;
  logoPath: string;
  mascotteAvatar: string;

  currentText: string;
  isSpinnerFetch: boolean;
  history: IAIBody = { elements: [] };

  isWelcomeSub: Subscription;
  feedSub: Subscription;

  constructor(
    private alertService: AlertService,
    private authService: AuthService,
    private chatAIService: ChatAIService,
    private modalController: ModalController,
    private profileService: ProfileService,
    private router: Router,
    private spinnerService: SpinnerService,
    private translateService: TranslateService,
    private storage: Storage) {
    this.isSpinnerFetch = true;
    this.logoPath = LOGO_CHAT_SPINNER;
    this.mascotteAvatar = BRAND_FAVICON;
  }

  async ngOnInit() {
    this.profile = await this.profileService.getProfile();

    this.isWelcomeSub = this.chatAIService.isWelcome$.subscribe((it: boolean) => {
      if (!it) {
        this.chatAIService.pushWelcomeMessage();
      }
    })
    
    this.feedSub = this.chatAIService.feed$.pipe(delay(330)) // TIP simulate round trip time
      .subscribe({
        next: (it: IAIBody) => {
          console.log('>>> feed', it);
          if (it) {
            const { elements = [], currentJSON: sendRouteParams } = it;

            if (elements.length > 0) {
              this.isSpinnerFetch = false;

              const lastUserMessage = elements[elements.length - 2]; // TIP take the last user message after AI write its message
              if (lastUserMessage && lastUserMessage.isLastUserMessage) {
                const { content = '' } = lastUserMessage;
                this.currentText = content;
              }
            }
              
            this.history = it;

            if (this.validateFinalJSONtoRoute(sendRouteParams)) {
              this.chatAIService.resetFinalJSON(); // TIP necessary to avoid a new json validation after next push
              this.chatAIService.pushStdMessage(
                { content: '', role: OpenAIRole.system, isConfirm: true }
              );

              this.spawnRouteRecap(sendRouteParams);
            }
          }
        },
        error: async (error) => { console.log('error', error); },
        complete: () => {},
      }
    );
  }

  ngOnDestroy() {
    if (this.isWelcomeSub) {
      this.isWelcomeSub.unsubscribe();
    }

    if (this.feedSub) {
      this.feedSub.unsubscribe();
    }
  }

  async sendMessage() { 
    this.chatAIService.pushStdMessage(
      { content: this.currentText, role: OpenAIRole.user }
    );
          
    try {
      await this.spinnerService.show();

      let token = ''; // TIP API is public, token can be empty

      try {
        token = await this.authService.getToken();
        if (!token) {
          token = ''; // TIP API is public, token can be empty
          const errorMessage = this.translateService.instant('ERRORS.UNAUTHORIZED');
          throw new Error(errorMessage);
        }
      } catch(error) {
        console.log('error: getTokenSilently has thrown exception or token does not exist');
      }

      this.chatAIService.pushStdMessage(
        { content: '', role: OpenAIRole.system, isLoading: true }
      );

      this.chatAIService.composeSendRoute(token);

      await this.spinnerService.dismiss();
    } catch (error) {
      this.pristineChat();

      await this.spinnerService.dismissWithAlertError(error);
    }

    this.currentText = '';
  }
  
  clearMessages() {
    const titleKey = 'AI_CHAT_ALERT_TITLE';
    const messageKey = 'AI_CHAT_ALERT_DESCRIPTION';
    const cancelHandler = () => {};
    const okHandler = async () => { this.pristineChat(); };
    
    this.alertService.confirm({ messageKey, titleKey, cancelHandler, okHandler });
  }

  pristineChat() {
    this.currentText = '';
    this.isSpinnerFetch = true;
    this.history.elements = [];

    this.chatAIService.resetChat();
    this.chatAIService.resetFinalJSON();
    this.chatAIService.resetRoute();
  }

  validateFinalJSONtoRoute(sendRouteParams: any) {
    if (sendRouteParams === null || sendRouteParams === undefined) return false;
    if (typeof sendRouteParams !== 'object') return false;
    console.log('>>> sendRouteParams', sendRouteParams);

    // TIP remove garbage from API
    delete sendRouteParams.details;
    delete sendRouteParams.product;
    delete sendRouteParams.packageDimension;

    // TIP keep it for retro compatibility
    if (!sendRouteParams.numberOfPackages) {
      delete sendRouteParams.numberOfPackages;
    } else {
      sendRouteParams.packagesNumber = sendRouteParams.numberOfPackages;
    }

    // TIP for now notes is not necessary
    delete sendRouteParams.notes;

    let tempStarDate: any, tempArrivalDate: any;
    if (!sendRouteParams.date) {
      tempStarDate = moment().toISOString();
      sendRouteParams.date = tempStarDate;
    } else {
      const m = moment(sendRouteParams.date);
      if (!m.isValid()) {
        tempStarDate = moment().toISOString();
        sendRouteParams.date = tempStarDate;
      } else {
        sendRouteParams.date = m.toISOString();
      }
    }
    if (!sendRouteParams.arrivalDate) {
      const arbitraryOffset = 7;
      tempArrivalDate = moment().add(arbitraryOffset, 'day').toISOString();
      sendRouteParams.arrivalDate = tempArrivalDate;
    } else {
      const m = moment(sendRouteParams.arrivalDate);
      if (!m.isValid()) {
        const arbitraryOffset = 7;
        tempArrivalDate = moment().add(arbitraryOffset, 'day').toISOString();
        sendRouteParams.arrivalDate = tempArrivalDate;
      } else {
        sendRouteParams.arrivalDate = m.toISOString();
      }
    }

    // Validazione routeObjects
    if (!sendRouteParams.routeObjects || !Array.isArray(sendRouteParams.routeObjects) || sendRouteParams.routeObjects.length === 0) {
      return false;
    }

    // Validazione di ogni routeObject
    for (let i = 0; i < sendRouteParams.routeObjects.length; i++) {
      const routeObject = sendRouteParams.routeObjects[i];
      
      // Assegna il nome se mancante
      if (!routeObject.name) {
        routeObject.name = `Pacco ${i + 1}`;
      }
      
      // Controlla che tutti i campi obbligatori siano presenti e validi
      if (!routeObject.width || routeObject.width <= 0) return false;
      if (!routeObject.height || routeObject.height <= 0) return false;
      if (!routeObject.length || routeObject.length <= 0) return false;
      if (!routeObject.weight || routeObject.weight <= 0) return false;
    }

    sendRouteParams.packageWeight = sendRouteParams.routeObjects.reduce(
      (acc, curr) => acc + (curr.weight || 0), 0
    ) || 0;

    return Object.values(sendRouteParams).every(it =>
      it !== null && it !== undefined && it !== ''
    );
  }

  async confirmManually(isConfirmed: boolean) {
    if (isConfirmed) {
      const sendRouteParams = this.chatAIService.getData();

      if (sendRouteParams) {
        this.spawnRouteRecap(sendRouteParams); 
      }
    }
  }

  async spawnRouteRecap(sendRouteParams: any) {
    this.chatAIService.setData(sendRouteParams);

    console.log('>>> sendRouteParams show', sendRouteParams);

    const modal = await this.modalController.create({
      backdropDismiss: true,
      showBackdrop: true,
      component: ModalSummaryTable,
      componentProps: {
        title: 'AI_CHAT_MODAL_TITLE',
        route: sendRouteParams,
      }
    });

    await this.saveSendObjectsToStorage(sendRouteParams.routeObjects as IRouteObjectDTO[]);

    modal.onDidDismiss().then(async ({ data }: any) => {
      if (!data || !data.confirm) {
        return;
      }

      await this.jumpBackToSendRoutePage();
    })
    .catch(() => console.log('error during dismiss'));

    return await modal.present();
  }

  async jumpBackToSendRoutePage() {
    const { url = '' } = this.router;
    this.chatAIService.setPage(url);

    await this.router.navigate(['/tabs/send'], { replaceUrl: true });
  }

  private async saveSendObjectsToStorage(sendObjects: IRouteObjectDTO[]): Promise<void> {
      try {
        console.log('Salvataggio di sendObjects nello storage:', sendObjects);
        await this.storage.set('send_objects', sendObjects);
        console.log('SendObjects salvati nello storage:', sendObjects);
      } catch (error) {
        console.error('Errore nel salvare sendObjects nello storage:', error);
      }
    }
}
